import { useMemo } from "react";
import { useSubmit } from "@certn/api";
import { showToast } from "@certn/cascadia-react";
import { Formik } from "formik";
import { t } from "i18next";
import { useNavigate } from "react-router-dom";
import { boolean, mixed, number, object, string } from "yup";

import { VerificationDetails } from "../../components/VerificationDetails";
import { VERIFICATION_TYPE } from "../../constants";
import { VerificationTemplatePageLayout } from "../VerificationTemplatePageLayout";
import {
  useVerificationTemplatesContext,
  VerificationTemplate,
} from "../VerificationTemplatesContext";
import { Schemas } from "../../../../types";
import omit from "lodash/omit";
import { EXPERIENCE_VERIFICATION_TEMPLATE_CONFIG } from "../../../experience-verification-templates/utils";
import {
  EMPLOYMENT_VERIFICATION_TEMPLATE_CONFIG,
  FCA_FIRM_REFERENCE_NUMBER_PATTERN,
} from "../../utils";
import { useFlags } from "launchdarkly-react-client-sdk";

export function VerificationTemplateDetails() {
  const { verificationTemplate } = useVerificationTemplatesContext();
  const navigate = useNavigate();
  const {
    releaseGapConfigForEmploymentVerification,
    enableGapDetectionInDaysForEmpVerification,
    enableFcaRegulatedReferences,
  } = useFlags();

  const { submit } = useSubmit(
    "/api/internal/client-portal/check-arguments-templates/employment-verification/create/",
    {
      throwErrors: true,
      showToastOnError: true,
      onSuccess: () => {
        showToast(t("Verification template has been created."), {
          type: "success",
        });
        navigate("/settings/employment-verification-templates");
      },
    }
  );

  const schema = object().shape({
    name: string().trim().min(1, "Name cannot be empty").required(),
    check_arguments: object().shape({
      validation_type: string().required().label(t("Validation type")),
      years: number()
        .label(t("Years"))
        .when("validation_type", {
          is: "YEARS",
          then: (schema) => schema.required(),
          otherwise: (schema) => schema.optional(),
        })
        .min(EMPLOYMENT_VERIFICATION_TEMPLATE_CONFIG.MINIMUM_YEARS)
        .max(EMPLOYMENT_VERIFICATION_TEMPLATE_CONFIG.MAXIMUM_YEARS),
      maximum_gap_period_in_days: string()
        .label(t("Maximum gap period"))
        .when("validation_type", {
          is: "YEARS",
          then: (schema) => schema.required(),
          otherwise: (schema) => schema.optional(),
        }),
      minimum: number()
        .label(t("Minimum"))
        .when("validation_type", {
          is: "QUANTITY",
          then: (schema) => schema.required(),
          otherwise: (schema) => schema.optional(),
        })
        .min(EMPLOYMENT_VERIFICATION_TEMPLATE_CONFIG.MINIMUM_EMPLOYERS_MINIMUM),
      maximum: number()
        .label(t("Maximum"))
        .when("validation_type", {
          is: "QUANTITY",
          then: (schema) => schema.required(),
          otherwise: (schema) => schema.optional(),
        })
        .max(EMPLOYMENT_VERIFICATION_TEMPLATE_CONFIG.MAXIMUM_EMPLOYERS_MAXIMUM),
      include_gap_detection: boolean().default(true),
      gap_threshold_unit: mixed<Schemas["GapThresholdUnitEnum"]>()
        .oneOf(["MONTHS", "DAYS"])
        .when(["validation_type", "include_gap_detection"], {
          is: (validationType: string, includeGapDetection: boolean) =>
            validationType === "YEARS" &&
            includeGapDetection !== false &&
            !!releaseGapConfigForEmploymentVerification &&
            !!enableGapDetectionInDaysForEmpVerification,
          then: (schema) =>
            schema.required(t("Select a unit for the minimum gap length.")),
          otherwise: (schema) => schema.optional().nullable(),
        }),
      minimum_gap_period_months: number()
        .transform((value, originalValue) =>
          originalValue === "" || originalValue == null ? undefined : value
        )
        .typeError(t("Enter a whole number."))
        .integer(t("Enter a whole number."))
        .min(EXPERIENCE_VERIFICATION_TEMPLATE_CONFIG.MINIMUM_GAP_PERIOD_MONTHS)
        .max(EXPERIENCE_VERIFICATION_TEMPLATE_CONFIG.MAXIMUM_GAP_PERIOD_MONTHS)
        .when(
          ["validation_type", "include_gap_detection", "gap_threshold_unit"],
          {
            is: (
              validationType: string,
              includeGapDetection: boolean,
              gapUnit: string | undefined
            ) =>
              validationType === "YEARS" &&
              includeGapDetection !== false &&
              !!releaseGapConfigForEmploymentVerification &&
              (!enableGapDetectionInDaysForEmpVerification ||
                gapUnit !== "DAYS"),
            then: (schema) =>
              schema.required(t("Enter the minimum gap length.")),
            otherwise: (schema) => schema.optional().nullable(),
          }
        ),
      minimum_gap_period_days: number()
        .transform((value, originalValue) =>
          originalValue === "" || originalValue == null ? undefined : value
        )
        .typeError(t("Enter a whole number."))
        .integer(t("Enter a whole number."))
        .min(EXPERIENCE_VERIFICATION_TEMPLATE_CONFIG.MINIMUM_GAP_PERIOD_DAYS)
        .max(EXPERIENCE_VERIFICATION_TEMPLATE_CONFIG.MAXIMUM_GAP_PERIOD_DAYS)
        .when(
          ["validation_type", "include_gap_detection", "gap_threshold_unit"],
          {
            is: (
              validationType: string,
              includeGapDetection: boolean,
              gapUnit: string | undefined
            ) =>
              validationType === "YEARS" &&
              includeGapDetection !== false &&
              !!releaseGapConfigForEmploymentVerification &&
              !!enableGapDetectionInDaysForEmpVerification &&
              gapUnit === "DAYS",
            then: (schema) =>
              schema.required(t("Enter the minimum gap length.")),
            otherwise: (schema) => schema.optional().nullable(),
          }
        ),
      gap_verification_type: mixed<Schemas["GapVerificationTypeEnum"]>()
        .oneOf(["REFERENCE", "STATEMENT"])
        .when(["validation_type", "include_gap_detection"], {
          is: (validationType: string, includeGapDetection: boolean) =>
            validationType === "YEARS" &&
            includeGapDetection !== false &&
            !!releaseGapConfigForEmploymentVerification,
          then: (schema) => schema.required(),
          otherwise: (schema) => schema.optional(),
        }),
      reference_rule_options: mixed<Schemas["ReferenceRuleOptionsEnum"]>()
        .oneOf(["DEFAULT"])
        .when(["include_gap_detection", "gap_verification_type"], {
          is: (includeGapDetection: boolean, gapType: string | undefined) =>
            includeGapDetection !== false && gapType === "REFERENCE",
          then: (schema) => schema.required(),
          otherwise: (schema) => schema.optional(),
        }),
      document_collection_mode: mixed<Schemas["DocumentCollectionModeEnum"]>()
        .oneOf(["MOST_RECENT", "START_AND_MOST_RECENT", "FULL_DURATION"])
        .optional()
        .nullable(),
      ...(enableFcaRegulatedReferences
        ? {
            fca_regulated_references_enabled: mixed<boolean>().optional(),
            fca_firm_reference_number: string()
              .trim()
              .when("fca_regulated_references_enabled", {
                is: true,
                then: (schema) =>
                  schema
                    .required(
                      t(
                        "This field is required when FCA regulated references are enabled."
                      )
                    )
                    .matches(
                      FCA_FIRM_REFERENCE_NUMBER_PATTERN,
                      t("Enter a valid Firm Reference Number (6–7 digits).")
                    ),
                otherwise: (schema) => schema.optional(),
              }),
          }
        : {}),
    }),
  });

  const checkArguments = verificationTemplate?.check_arguments;
  const isQuantity =
    checkArguments?.validation_type === VERIFICATION_TYPE.quantity;

  const formInitialValues = useMemo(
    () => ({
      ...verificationTemplate,
      check_arguments: {
        ...verificationTemplate?.check_arguments,
        fca_regulated_references_enabled: Boolean(
          verificationTemplate?.check_arguments?.fca_firm_reference_number
        ),
      },
    }),
    [verificationTemplate]
  );

  const createTemplate = async ({
    name,
    check_arguments,
  }: VerificationTemplate) => {
    const keysToOmit: string[] = ["fca_regulated_references_enabled"];

    if (!enableFcaRegulatedReferences) {
      keysToOmit.push("fca_firm_reference_number");
    }

    const includeGapDetection = check_arguments.include_gap_detection !== false;

    if (!releaseGapConfigForEmploymentVerification) {
      keysToOmit.push(
        "include_gap_detection",
        "gap_verification_type",
        "minimum_gap_period_months",
        "minimum_gap_period_days",
        "gap_threshold_unit",
        "reference_rule_options"
      );
    } else if (check_arguments.validation_type === VERIFICATION_TYPE.quantity) {
      keysToOmit.push(
        "years",
        "include_gap_detection",
        "gap_verification_type",
        "minimum_gap_period_months",
        "minimum_gap_period_days",
        "gap_threshold_unit",
        "reference_rule_options"
      );
    } else if (!includeGapDetection) {
      keysToOmit.push(
        "gap_verification_type",
        "minimum_gap_period_months",
        "minimum_gap_period_days",
        "gap_threshold_unit",
        "reference_rule_options"
      );
    } else {
      keysToOmit.push("minimum", "maximum", "include_gap_detection");
    }

    const omittedCheckArguments: Record<string, unknown> = omit(
      check_arguments,
      keysToOmit
    );

    if (
      releaseGapConfigForEmploymentVerification &&
      check_arguments.validation_type === VERIFICATION_TYPE.years &&
      !includeGapDetection
    ) {
      omittedCheckArguments.include_gap_detection = false;
    }

    // Formik stores TextInputFormikField and SelectItem values as strings.
    // The API schema requires `minimum_gap_period_months` / `minimum_gap_period_days`
    // as numbers, so coerce here before submitting.
    for (const key of [
      "minimum_gap_period_months",
      "minimum_gap_period_days",
    ] as const) {
      const raw = omittedCheckArguments[key];
      if (raw != null && raw !== "") {
        omittedCheckArguments[key] = Number(raw);
      }
    }

    await submit({
      name,
      check_arguments: omittedCheckArguments,
    });
  };

  return (
    <Formik<VerificationTemplate>
      initialValues={formInitialValues}
      validationSchema={schema}
      onSubmit={async (values) => await createTemplate(values)}
      enableReinitialize
      validateOnMount
    >
      {({ handleSubmit, isSubmitting, values, setValues, isValid }) => {
        return (
          <VerificationTemplatePageLayout
            onComplete={handleSubmit}
            submitting={isSubmitting}
            disableCompletion={!isValid}
          >
            <VerificationDetails
              isQuantity={isQuantity}
              checkArguments={values.check_arguments}
              setValues={setValues}
            />
          </VerificationTemplatePageLayout>
        );
      }}
    </Formik>
  );
}
